/**
 * Dashboard API Service
 * Fetches dashboard statistics and activity
 */

import { sessionManagerApi, labManagerApi, environmentOrchestratorApi } from './apiClient';
import { mockFallback } from '../lib/mockFallback';
import { getAtRiskStudents } from './atRiskApi';
import type { DashboardStats, ActivityItem, ProgressTrend, LabPerformance } from '../types';

/**
 * Get dashboard statistics
 */
export async function getDashboardStats(): Promise<DashboardStats> {
  try {
    // Fetch from multiple services in parallel
    const [studentsRes, labsRes, envsRes, gradingRes] = await Promise.all([
      sessionManagerApi.get('/admin/students/stats').catch(() => ({ data: {} })),
      labManagerApi.get('/instructor/labs/stats').catch(() => ({ data: {} })),
      environmentOrchestratorApi.get('/resources/usage').catch(() => ({ data: {} })),
      labManagerApi.get('/grading/stats').catch(() => ({ data: {} })),
    ]);

    return {
      totalStudents: studentsRes.data.total || 0,
      activeStudents: studentsRes.data.active || 0,
      newStudentsThisWeek: studentsRes.data.newThisWeek || 0,
      totalLabs: labsRes.data.total || 0,
      labsPublished: labsRes.data.published || 0,
      submissionsPending: gradingRes.data.pending || 0,
      activeEnvironments: envsRes.data.runningEnvironments || 0,
      totalEnvironments: envsRes.data.totalEnvironments || 0,
      averageCompletionRate: labsRes.data.avgCompletionRate || 0,
      averageScore: gradingRes.data.averageScore || 0,
    };
  } catch (error) {
    console.error('Failed to fetch dashboard stats:', error);
    const mock: DashboardStats = {
      totalStudents: 156,
      activeStudents: 42,
      newStudentsThisWeek: 12,
      totalLabs: 48,
      labsPublished: 36,
      submissionsPending: 18,
      activeEnvironments: 15,
      totalEnvironments: 89,
      averageCompletionRate: 72,
      averageScore: 84,
    };
    return mockFallback(error, mock);
  }
}

/**
 * Get recent activity feed
 */
export async function getRecentActivity(limit: number = 20): Promise<ActivityItem[]> {
  try {
    const response = await labManagerApi.get('/instructor/activity', { params: { limit } });
    return response.data.activities;
  } catch (error) {
    console.error('Failed to fetch activity:', error);
    const mock: ActivityItem[] = [
      {
        id: '1',
        type: 'submission',
        message: 'submitted "Building RAG Applications" for grading',
        studentName: 'John Doe',
        labTitle: 'Building RAG Applications',
        timestamp: new Date(Date.now() - 1000 * 60 * 15).toISOString(),
      },
      {
        id: '2',
        type: 'completion',
        message: 'completed "Introduction to LangChain"',
        studentName: 'Jane Smith',
        labTitle: 'Introduction to LangChain',
        timestamp: new Date(Date.now() - 1000 * 60 * 45).toISOString(),
      },
      {
        id: '3',
        type: 'start',
        message: 'started "Fine-tuning Language Models"',
        studentName: 'Bob Johnson',
        labTitle: 'Fine-tuning Language Models',
        timestamp: new Date(Date.now() - 1000 * 60 * 90).toISOString(),
      },
      {
        id: '4',
        type: 'environment',
        message: 'launched a new development environment',
        studentName: 'Alice Brown',
        timestamp: new Date(Date.now() - 1000 * 60 * 120).toISOString(),
      },
      {
        id: '5',
        type: 'completion',
        message: 'completed "OpenAI API Basics" with score 95%',
        studentName: 'Charlie Wilson',
        labTitle: 'OpenAI API Basics',
        timestamp: new Date(Date.now() - 1000 * 60 * 180).toISOString(),
      },
    ];
    return mockFallback(error, mock);
  }
}

/**
 * Get progress trends over time
 */
export async function getProgressTrends(days: number = 14): Promise<ProgressTrend[]> {
  try {
    const response = await labManagerApi.get('/instructor/analytics/trends', { params: { days } });
    return response.data.trends;
  } catch (error) {
    console.error('Failed to fetch trends:', error);
    const trends: ProgressTrend[] = [];
    const now = new Date();
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      trends.push({
        date: date.toISOString().split('T')[0],
        completions: Math.floor(Math.random() * 15) + 5,
        submissions: Math.floor(Math.random() * 20) + 8,
        newEnrollments: Math.floor(Math.random() * 8) + 2,
      });
    }
    return mockFallback(error, trends);
  }
}

/**
 * Get lab performance metrics
 */
export async function getLabPerformance(): Promise<LabPerformance[]> {
  try {
    const response = await labManagerApi.get('/instructor/analytics/labs');
    return response.data.labs;
  } catch (error) {
    console.error('Failed to fetch lab performance:', error);
    const mock: LabPerformance[] = [
      {
        labId: '1',
        labTitle: 'Introduction to OpenAI API',
        completions: 45,
        submissions: 52,
        averageScore: 87,
        passRate: 92,
        averageTimeSpent: 45,
      },
      {
        labId: '2',
        labTitle: 'Building RAG Applications',
        completions: 28,
        submissions: 35,
        averageScore: 78,
        passRate: 85,
        averageTimeSpent: 90,
      },
      {
        labId: '3',
        labTitle: 'Fine-tuning Language Models',
        completions: 15,
        submissions: 22,
        averageScore: 72,
        passRate: 78,
        averageTimeSpent: 120,
      },
      {
        labId: '4',
        labTitle: 'LangChain Fundamentals',
        completions: 38,
        submissions: 42,
        averageScore: 85,
        passRate: 90,
        averageTimeSpent: 60,
      },
    ];
    return mockFallback(error, mock);
  }
}

/**
 * Get just the count of at-risk students (cheap signal for nav badges).
 *
 * The session-manager exposes a count-only endpoint to avoid shipping the
 * full per-student payload when all the caller wants is a badge number.
 * Falls back to counting the full list (which itself has a mock fallback)
 * so this is safe to call in mock/dev mode.
 */
export async function getAtRiskCount(): Promise<number> {
  try {
    const response = await sessionManagerApi.get('/instructor/at-risk/count');
    return Number(response.data.count ?? 0);
  } catch (error) {
    console.error('Failed to fetch at-risk count:', error);
    // Fallback: derive from the full list (which has its own mock path).
    try {
      const list = await getAtRiskStudents();
      return list.length;
    } catch {
      return mockFallback(error, 0);
    }
  }
}
